import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartMixed, faDesktop } from '@fortawesome/pro-regular-svg-icons';
import { mobile } from '@assets/icons';
import { Card, Column, ColumnGroup } from '@cards/Card';
import SubNavMenu from '@components/SubNavMenu';
import SubNavMenuContainer from '@components/SubNavMenuContainer';
import { usePageActions } from '@hooks';
import { loadDirectChildOrganisation } from '@redux/directChildOrganisationAdministration/actions';
import { selectDirectChildOrganisation } from '@redux/directChildOrganisationAdministration/selectors';
import { MixpanelOrganisationConfig } from '@integrations/mixpanel/pages/EditOrganisationConfig';
import { DirectChildOrganisationKemtaiConfigForm } from '@integrations/kemtai/pages/DirectChildOrganisationConfig';
import { setCurrentScreen, screens } from '../../../analytics';
import '../directChildOrganisationAdministration.less';

const DirectChildOrganisationDetails = ({ match }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { organisationSlug } = match.params;
  const organisation = useSelector(state => selectDirectChildOrganisation(state, organisationSlug));
  const organisationName = organisation.data?.name || '';
  const loading = organisation.status === 'idle' || organisation.status === 'loading';
  const { hasPageActionsProvider, setTitle } = usePageActions({
    title: organisationName,
    actions: [],
    showBack: true,
    onBack: () => history.push('/administration/child-organisations'),
  });

  useEffect(() => {
    setTitle(organisationName);
    if (organisationName) document.title = organisationName;
  }, [organisationName, setTitle]);

  useEffect(() => {
    dispatch(loadDirectChildOrganisation(organisationSlug));
  }, [dispatch, organisationSlug]);

  useEffect(() => {
    const screenByRoute = {
      setup: screens.ADMINISTRATION_DIRECT_CHILD_ORGANISATION_SETUP,
      kemtai: screens.ADMINISTRATION_DIRECT_CHILD_ORGANISATION_KEMTAI,
      analytics: screens.ADMINISTRATION_DIRECT_CHILD_ORGANISATION_ANALYTICS,
    };
    const route = location.pathname.split('/').pop();

    setCurrentScreen(
      screenByRoute[route] || screens.ADMINISTRATION_DIRECT_CHILD_ORGANISATION_SETUP,
    );
  }, [location.pathname]);

  if (organisation.status === 'failure') {
    const errorKeyByStatus = {
      403: 'forbidden',
      404: 'notFound',
      502: 'upstream',
    };
    const errorKey = errorKeyByStatus[organisation.errorStatus] || 'failure';

    return (
      <section role="alert">
        <p>{t(`directChildOrganisationAdministration:organisations.details.${errorKey}`)}</p>
        <button
          type="button"
          onClick={() => dispatch(loadDirectChildOrganisation(organisationSlug))}
        >
          {t('directChildOrganisationAdministration:organisations.details.retry')}
        </button>
      </section>
    );
  }

  return (
    <>
      {!hasPageActionsProvider ? (
        <>
          <Link to="/administration/child-organisations">
            {t('directChildOrganisationAdministration:organisations.details.back')}
          </Link>
          {!loading ? <h1>{organisationName}</h1> : null}
        </>
      ) : null}
      <SubNavMenuContainer
        className="direct-child-organisation-details"
        menu={
          <SubNavMenu>
            <Menu.Item key="setup">
              <FontAwesomeIcon icon={faDesktop} />
              {t('directChildOrganisationAdministration:organisations.details.setup')}
            </Menu.Item>
            <Menu.Item key="kemtai">
              <FontAwesomeIcon icon={mobile} />
              {t('directChildOrganisationAdministration:organisations.details.kemtai')}
            </Menu.Item>
            <Menu.Item key="analytics">
              <FontAwesomeIcon icon={faChartMixed} />
              {t('directChildOrganisationAdministration:organisations.details.analytics')}
            </Menu.Item>
          </SubNavMenu>
        }
      >
        <Switch>
          <Route exact path={`${match.path}/setup`}>
            {!hasPageActionsProvider ? (
              <h2>{t('directChildOrganisationAdministration:routes.organisationSetup')}</h2>
            ) : null}
            {hasPageActionsProvider || loading ? (
              <ColumnGroup>
                <Column>
                  <Card.Half
                    className="direct-child-organisation-setup-card"
                    loading={loading}
                    title={t(
                      'organisations:OrganisationDetails.DashboardSetup.AppAdminDetailsCard.title',
                    )}
                  >
                    {!loading ? <p>{organisationName}</p> : null}
                  </Card.Half>
                </Column>
                <Column />
              </ColumnGroup>
            ) : null}
          </Route>
          <Route exact path={`${match.path}/kemtai`}>
            {loading ? (
              <Card loading />
            ) : (
              <DirectChildOrganisationKemtaiConfigForm
                key={organisation.data.slug}
                directChildOrganisation={organisation.data}
              />
            )}
          </Route>
          <Route exact path={`${match.path}/analytics`}>
            {loading ? (
              <Card loading />
            ) : (
              <MixpanelOrganisationConfig
                key={organisation.data.slug}
                organisation={organisation.data}
              />
            )}
          </Route>
        </Switch>
      </SubNavMenuContainer>
    </>
  );
};

export default DirectChildOrganisationDetails;
